/**
 * @file live-issues-store.ts
 * @description Кэш ответа `GET admin/site-view/live-issues` — «без чего сайт
 * работает сейчас» (phase19.16, SB125; партия SB120, этап 2 —
 * uiux-sb120-stage2 §4, db-design-sb120-stage2 §6; TD-407): одна запись на
 * вкладку кабинета, свежая 60 с, затем перечитывается при следующем показе
 * плашки. Запись помечена кабинетом (контекстом магазина): запись чужого
 * кабинета несвежая и не показывается (ревью: кэш был общий для магазинов).
 * Сброс (`invalidateLiveSiteIssues`) — после публикации и отката сайта,
 * правки реквизитов и правовых документов (их зовут API-клиенты) и смены
 * кабинета вкладки (`selectTabContext` в AuthProvider).
 *
 * Без зависимостей от API-клиента (его модуль сам зовёт сброс — без цикла
 * импортов). Подписка — для `useSyncExternalStore` (`useLiveSiteIssues`).
 */

import type { PublishCheck } from "./types";

/** Ответ сервера: опубликован ли сайт и ошибки по данным магазина (уровень `error`). */
export interface SiteLiveIssues {
  published: boolean;
  errors: PublishCheck[];
}

/** Сколько ответ считается свежим (§6: staleTime 60 с). */
export const LIVE_ISSUES_STALE_MS = 60_000;

/** Запись кэша: чей кабинет, данные, ошибка запроса и когда получены. */
export interface LiveIssuesEntry {
  /** Контекст (кабинет магазина), для которого получен ответ. */
  key: string;
  data: SiteLiveIssues | null;
  error: boolean;
  at: number;
}

let entry: LiveIssuesEntry | null = null;
let generation = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Текущая запись (`null` — не загружено или сброшено). */
export function liveIssuesEntry(): LiveIssuesEntry | null {
  return entry;
}

/** Номер сброса: ответ запроса, начатого до сброса, не записывается. */
export function liveIssuesGeneration(): number {
  return generation;
}

/** Записать ответ (или ошибку) запроса кабинета `key`, начатого при `startedAt` поколении. */
export function storeLiveIssues(startedAt: number, key: string, data: SiteLiveIssues | null, error: boolean, now = Date.now()): void {
  if (startedAt !== generation) return;
  entry = { key, data, error, at: now };
  emit();
}

/** Свежая ли запись кабинета `key` (запись другого кабинета — несвежая). */
export function liveIssuesFresh(key: string, now = Date.now()): boolean {
  return entry !== null && entry.key === key && now - entry.at < LIVE_ISSUES_STALE_MS;
}

/** Сбросить кэш: следующий показ плашки перечитает сервер. */
export function invalidateLiveSiteIssues(): void {
  entry = null;
  generation += 1;
  emit();
}

/** Подписка на смену кэша; возвращает отписку. */
export function subscribeLiveIssues(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
